import debounce from 'lodash/debounce';
import dynamic from 'next/dynamic';
import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { clearSearchFiltersForPdp } from 'store/slice/searchSlice';
import { FeatureFlags } from 'featureFlags/constants';
import { fireSnapchatPDPView } from 'tracking/utils/snapchatEvents';
import { fireTiktokPDPView } from 'tracking/utils/tiktokEvents';
import { getSizesMap } from 'productTemplate/utils/getSizesMap';
import { getWants } from 'productTemplate/services/wants';
import {
  IConsumerSearchFactsWindowData,
  IRelatedProductData,
} from 'productTemplate/types/FactsWindowData';
import { IFeaturedInCollection } from 'productTemplate/types/FeaturedInCollection';
import { IGmcOffers } from 'shared/types/Offer';
import { IProductTemplate } from 'shared/types/ProductTemplate';
import { LOCALES } from 'localization/constants/configEnums';
import { logToFriendlyLogs } from 'shared/services/logToFriendlyLogs';
import { PRODUCT_TYPES } from 'productTemplate/enums/productType';
import { setBuyBarSizesMap } from 'store/slice/buyBarSlice';
import { setBuyBarVariants, setProductTemplateData, setUserWants } from 'store/slice/pdpSlice';
import { setIsFooterVisible } from 'store/slice/pageLayoutSlice';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { useFetchBuyBarVariantsQuery } from 'store/api/buyBarVariantsApi';
import { useLocation } from 'shared/hooks/useLocation';
import { useStoreDispatch, useStoreSelector } from 'store/hooks/storeHooks';
import { useWindowSize } from 'shared/hooks/useWindowSize';
import { sendTrackingPdpView } from 'productTemplate/utils/sendPdpViewTrackingEvent';
import { useMParticleContext } from 'tracking/hooks/useMParticleContext';

const ProductDetailPageLanding = dynamic(
  () => import('productTemplate/components/ProductDetailPageLanding'),
  { ssr: true },
);

const ProductDetailPageMobile = dynamic(
  () =>
    import('productTemplate/components/ProductDetailPageMobile').then(
      (mod) => mod.ProductDetailPageMobile,
    ),
  { ssr: true },
);

interface IProductDetailPage {
  factsWindowData: IConsumerSearchFactsWindowData;
  featuredInCollections: IFeaturedInCollection[];
  isCrawler: boolean;
  isMobileUserAgent: boolean;
  locale?: LOCALES;
  offers: IGmcOffers;
  productTemplate: IProductTemplate;
  recommendedProducts: Array<IRelatedProductData>;
}

const ProductDetailPage = ({
  factsWindowData,
  featuredInCollections,
  isCrawler = false,
  isMobileUserAgent,
  offers,
  productTemplate,
  locale,
  recommendedProducts,
}: IProductDetailPage) => {
  /* store */
  const storeDispatch = useStoreDispatch();
  const { isAuthenticated, user, clientDedupID } = useStoreSelector((state) => state.auth);
  /* state */
  const [showDesktopPdp, setShowDesktopPdp] = useState(!isCrawler && !isMobileUserAgent);
  /* constants */
  const {
    brandName,
    id,
    isAvailabilityRestricted,
    lowestPriceCents,
    name,
    sizeOptions,
    sku,
    slug,
  } = productTemplate;
  /* feature flags */
  const isSnapchatEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_SNAPCHAT);
  const isTiktokEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_TIKTOK);
  /* hooks */
  const router = useRouter();
  const { asPath, query, pathname } = router;
  const { isTablet, isDesktop } = useWindowSize();
  const { selectedCountryCode } = useLocation();
  const initialRender = useRef(false);
  const { data: buyBarVariantsData, isSuccess } = useFetchBuyBarVariantsQuery(
    {
      id,
      selectedCountryCode,
    },
    { skip: isAvailabilityRestricted, refetchOnMountOrArgChange: true },
  );
  const { isMParticleReady } = useMParticleContext();
  if (!initialRender.current) {
    storeDispatch(
      setProductTemplateData({
        factsWindowData,
        featuredInCollections,
        productTemplate,
      }),
    );
    initialRender.current = true;
  }

  useEffect(() => {
    storeDispatch(
      setProductTemplateData({
        factsWindowData,
        featuredInCollections,
        productTemplate,
      }),
    );
  }, [id]);

  useEffect(() => {
    if (isSuccess) {
      // New Mobile PDP
      storeDispatch(setBuyBarSizesMap(getSizesMap(buyBarVariantsData, sizeOptions)));
      // Destkop & Deprecated Mobile PDP
      storeDispatch(setBuyBarVariants(buyBarVariantsData));
    }
  }, [buyBarVariantsData, isSuccess, sizeOptions]);

  useEffect(() => {
    const fetchWants = async () => {
      const wants = await getWants(id);
      if (wants) {
        storeDispatch(setUserWants(wants));
      }
    };

    if (isAuthenticated) {
      void fetchWants();
    }

    if (!isAuthenticated) {
      storeDispatch(setUserWants([]));
    }
  }, [id, isAuthenticated]);

  useEffect(() => {
    // This prevents a huge layout shift on the mobile pdp. We add the Footer in the PDP
    storeDispatch(setIsFooterVisible(false));

    return () => {
      storeDispatch(setIsFooterVisible(true));
    };
  }, []);

  useEffect(() => {
    const handleRouteChange = (url) => {
      // We do not want to clear the filters when we navigate between sneakers and apparel PDP
      // If route we're changing to is no longer a PDP, clear the search filters
      const urlSplit = url.split('/');
      const isSneakersOrApparel = new Set(['apparel', 'sneakers']);
      const isNotSneakersOrApparelCategory =
        !PRODUCT_TYPES.sneakers.has(urlSplit?.[2]) || !PRODUCT_TYPES.apparel.has(urlSplit?.[2]);
      const isUrlPdp =
        urlSplit.length === 3 &&
        isSneakersOrApparel.has(urlSplit?.[1]) &&
        isNotSneakersOrApparelCategory;

      if (!isUrlPdp) {
        storeDispatch(clearSearchFiltersForPdp());
      }
    };

    router.events.on('routeChangeStart', handleRouteChange);

    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [router]);

  // measuring page load speed and sending it to Loki
  useEffect(() => {
    const handleLoad = async () => {
      const pageLoaded = performance.mark('mobile-pdp-loaded');
      if (pageLoaded) {
        const loadTimeInSeconds = pageLoaded?.startTime / 1000;
        const hasLongNetworkLatency = Math.round(loadTimeInSeconds) > 15;
        if (loadTimeInSeconds > 0 && !hasLongNetworkLatency) {
          await logToFriendlyLogs({
            'x-goat-mobile-pdp-view': 'new',
            'x-goat-mobile-pdp-load-time': loadTimeInSeconds.toFixed(3),
          });
        }
      }
    };

    if (!isTablet && !isDesktop && !isCrawler) {
      window.addEventListener('load', handleLoad);
    }

    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, [isTablet, isDesktop, isCrawler]);

  const debouncedSnapchatPDPView = debounce((retry = 5, previousTimeout = null) => {
    const { status: fireSnapchatPDPViewStatus } = fireSnapchatPDPView({
      lowestPriceCents,
      sku,
      brandName,
      clientDedupID,
    });

    if (previousTimeout) {
      clearTimeout(previousTimeout);
    }

    if (fireSnapchatPDPViewStatus !== 'success' && retry > 0) {
      const timeOutID = setTimeout(() => {
        debouncedSnapchatPDPView(retry - 1, timeOutID);
      }, 1000);
    }
  }, 100);

  const debouncedTiktokPDPView = debounce(() => {
    fireTiktokPDPView(
      {
        contentCategory: brandName,
        contentId: sku,
        contentName: name,
        price: lowestPriceCents,
      },
      user?.email,
    );
  }, 100);

  useEffect(() => {
    if (isSnapchatEnabled) {
      debouncedSnapchatPDPView();
    }

    if (isTiktokEnabled) {
      debouncedTiktokPDPView();
    }
  }, [slug]);

  useEffect(() => {
    if (isTablet || isDesktop) {
      setShowDesktopPdp(true);
    } else {
      setShowDesktopPdp(false);
    }
  }, [isTablet, isDesktop]);

  const prevPath =
    typeof sessionStorage !== 'undefined' ? sessionStorage?.getItem('prevPath') : null;
  const currentPath =
    typeof sessionStorage !== 'undefined' ? sessionStorage?.getItem('currentPath') : null;

  // Send pdp view event when the page is loaded
  useEffect(() => {
    // on initial page load the prevPath is still the currentPath so wait until currentPath is asPath
    if (currentPath === asPath && isMParticleReady) {
      sendTrackingPdpView({ prevPath, id, name, slug, brandName, query, sku });
    }
  }, [currentPath, prevPath, asPath, isMParticleReady, id, query, pathname, sku]);

  return !isCrawler && showDesktopPdp ? (
    <ProductDetailPageLanding
      isCrawler={isCrawler}
      offers={offers}
      productTemplate={productTemplate}
      locale={locale}
    />
  ) : (
    <ProductDetailPageMobile
      productTemplate={productTemplate}
      recommendedProducts={recommendedProducts}
    />
  );
};

export default ProductDetailPage;
